import type { Modifier, Placement } from "@popperjs/core";
import { createPopper, detectOverflow } from "@popperjs/core";
import { onBeforeUnmount, ref } from "vue";

export interface PopperOptions {
  placement: Placement;
  offset: { left: number; top: number };
  // Avoid flipping bottom<->top; instead move the contents up to fit the window.
  noFlip: boolean;
  // Skip the height-clamp modifiers and let the content size itself. Driven by DropdownMenu's
  // maxHeight="content". Use for popups whose content already manages its own height (own
  // max-height + internal scroll), where the clamp would otherwise fight that and lock the popup
  // to a stale (e.g. loading-state) height.
  fitContent: boolean;
}

export function usePopper(getOptions: () => PopperOptions) {
  const instance = ref<ReturnType<typeof createPopper> | null>(null);
  let resizeObserver: ResizeObserver | null = null;

  // Move the popup up when it overflows the bottom instead of flipping
  const move: Modifier<"move", Record<string, never>> = {
    name: "move",
    enabled: true,
    phase: "main",
    requiresIfExists: ["offset"],
    fn({ state }) {
      // On a left/right placement, popper's preventOverflow already shifts along this axis
      if (isSidePlacement(state.placement)) return;
      const overflow = detectOverflow(state, {});
      if (overflow.bottom > 0 && state.modifiersData.popperOffsets) {
        state.modifiersData.popperOffsets.y -= overflow.bottom;
      }
    },
  };

  // Measure the space available above/below the trigger and feed the resulting popper height
  // into state.rects before flip runs.
  const maxSize: Modifier<"maxSize", Record<string, never>> = {
    name: "maxSize",
    enabled: true,
    phase: "beforeMain",
    requiresIfExists: ["offset"],
    fn({ state, name }) {
      const rect = state.elements.reference.getBoundingClientRect();
      const gap = getOptions().offset.top;
      const viewportHeight = document.documentElement.clientHeight;
      const spaceAbove = rect.top - gap;
      const spaceBelow = viewportHeight - rect.bottom - gap;
      const available = Math.max(spaceAbove, spaceBelow);
      state.rects.popper.height = Math.min(state.elements.popper.scrollHeight, available);
      state.modifiersData[name] = { available };
    },
  };

  const applyMaxSize: Modifier<"applyMaxSize", Record<string, never>> = {
    name: "applyMaxSize",
    enabled: true,
    phase: "beforeWrite",
    requires: ["maxSize"],
    // A variable, not an inline max-height: inline would override BasePopup's max-height token.
    // Set directly because popper writes state.styles with Object.assign, which drops custom properties.
    fn({ state }) {
      state.elements.popper.style.setProperty(
        "--popper-max-height",
        `${state.modifiersData.maxSize.available}px`,
      );
    },
  };

  function create(trigger: Element | undefined | null, content: Element | undefined | null) {
    if (trigger && content) {
      const { placement, offset, noFlip, fitContent } = getOptions();
      instance.value = createPopper(trigger, content as HTMLElement, {
        // fixed strategy to not flicker search dropdowns when zooming, might break in other places?
        strategy: "fixed",
        placement,
        modifiers: [
          { name: "offset", options: { offset: [offset.left, offset.top] } },
          // noFlip: pin the side and shift up to fit. fitContent: keep the default flip but let
          // the content size itself. Otherwise: flip + clamp the height to the viewport.
          ...(noFlip
            ? [{ name: "flip", enabled: false }, move]
            : fitContent
              ? []
              : [maxSize, applyMaxSize]),
        ],
      });

      // Reposition when the content resizes after opening (e.g. async data replacing a loading state).
      resizeObserver = new ResizeObserver(() => void instance.value?.update());
      resizeObserver.observe(content);
    }
  }

  function destroy() {
    resizeObserver?.disconnect();
    resizeObserver = null;
    instance.value?.destroy();
    instance.value = null;
  }

  onBeforeUnmount(destroy);

  return { instance, create, destroy };
}

function isSidePlacement(placement: Placement) {
  return placement.startsWith("left") || placement.startsWith("right");
}
